import React from 'react';
import { motion } from 'framer-motion';

/**
 * Splits a string into words and reveals them with a stagger. Each word slides
 * up + fades in. Great for hero H1s. Preserves inline nodes: pass children,
 * strings are split, React elements pass through as-is (useful for wrapping
 * one word in a FlickerReveal / AuroraText).
 *
 * Props:
 *   text (string)       — words to split
 *   children (nodes)    — mixed strings + elements; strings are word-split
 *   delay               — start delay
 *   stagger             — per-word stagger (default 0.08)
 *   run                 — gate visibility on external ready flag
 */
const parent = (delay, stagger) => ({
  hidden: {},
  visible: { transition: { staggerChildren: stagger, delayChildren: delay } },
});
const child = {
  hidden: { opacity: 0, y: '110%' },
  visible: { opacity: 1, y: '0%', transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const Word = ({ children: c }) => (
  <span className="inline-block overflow-hidden align-baseline" style={{ verticalAlign: 'top' }}>
    <motion.span variants={child} className="inline-block">
      {c}
    </motion.span>
  </span>
);

const splitNode = (node, keyPrefix) => {
  if (typeof node === 'string') {
    return node
      .split(/(\s+)/)
      .filter((s) => s !== '')
      .map((seg, i) => {
        if (/^\s+$/.test(seg)) return <span key={`${keyPrefix}-s-${i}`}>{seg}</span>;
        return <Word key={`${keyPrefix}-w-${i}`}>{seg}</Word>;
      });
  }
  return <Word key={keyPrefix}>{node}</Word>;
};

const WordReveal = ({
  children,
  text,
  delay = 0,
  stagger = 0.08,
  run = true,
  as: Tag = 'span',
  className = '',
  style = {},
}) => {
  const items = text
    ? [text]
    : React.Children.toArray(children);
  return (
    <motion.span
      className={className}
      style={style}
      variants={parent(delay, stagger)}
      initial="hidden"
      animate={run ? 'visible' : 'hidden'}
    >
      {items.flatMap((n, i) => {
        const out = splitNode(n, `n${i}`);
        return Array.isArray(out) ? out : [out];
      })}
    </motion.span>
  );
};

export default WordReveal;
